import React from "react";
import { Box, Skeleton, Stack, Toolbar } from "@mui/material";
import { useIsMobile } from "@tlc/ui/useIsMobile";

export interface FinesToolbarSkeletonProps {
  hideActions?: boolean;
  ecommerceEnabled: boolean;
}

export const FinesToolbarSkeleton: React.FC<FinesToolbarSkeletonProps> = ({
  hideActions = true,
  ecommerceEnabled,
}) => {
  const isMobile = useIsMobile();
  return (
    <>
      <Box display="flex" flexDirection="column" sx={{ p: 0 }}>
        {/* --- First Row --- */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 2,
            py: 2,
            minHeight: "40px",
          }}
        >
          {/* Title */}
          <Skeleton
            variant="rounded"
            width="100%"
            height={32}
            sx={{ maxWidth: 220, ml: 1 }}
          />

          {!hideActions && ecommerceEnabled && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flexGrow: 1,
                alignItems: "flex-end",
                justifyContent: "center",
                gap: 0.5,
              }}
            >
              {/* Pay Fines Button */}
              <Skeleton variant="rounded" width={100} height={32} />
              {/* Convenience Fee */}
              <Skeleton variant="text" width={200} height={20} />
            </Box>
          )}
        </Box>

        {/* --- Second Row --- */}
        <Toolbar
          disableGutters
          sx={{
            flexWrap: "wrap",
            flexDirection: "row",
            alignItems: "center",
            ml: 1,
            pl: 0.5,
            pr: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flex: "1 1 100%",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 2,
            }}
          >
            {/* Print Button */}
            {!hideActions && (
              <Skeleton
                variant="rounded"
                width={115}
                height={36}
                sx={{ ml: isMobile ? 1 : 0 }}
              />
            )}
            {/* Sort Dropdown */}
            <Skeleton variant="rounded" width={100} height={36} />
          </Box>
        </Toolbar>
      </Box>
    </>
  );
};

export interface FinesGridSkeletonProps {
  pageSize?: number;
}

export const FinesGridSkeleton: React.FC<FinesGridSkeletonProps> = ({
  pageSize = 10,
}) => {
  const isMobile = useIsMobile();
  return (
    <>
      {/* --- Third Row --- */}
      {isMobile ? (
        // Mobile view card skeletons
        <Box pt={1} pl={2}>
          <Stack spacing={2} width="100%" pb={2}>
            {Array.from({ length: pageSize }, (_, i) => {
              return (
                <Skeleton key={i} variant="rounded" height={100} width="100%" />
              );
            })}
          </Stack>
        </Box>
      ) : (
        // Desktop view grid skeletons
        <Box pt={2} pl={1}>
          {Array.from({ length: pageSize }, (_, i) => {
            return <Skeleton key={i} sx={{ mb: 1 }} height={32} />;
          })}
        </Box>
      )}
    </>
  );
};

export interface FinesSkeletonsProps {
  hideActions: boolean;
  ecommerceEnabled: boolean;
  pageSize: number;
}

export const FinesSkeltons: React.FC<FinesSkeletonsProps> = ({
  hideActions = true,
  ecommerceEnabled,
  pageSize = 10,
}) => {
  return (
    <>
      <FinesToolbarSkeleton
        hideActions={hideActions}
        ecommerceEnabled={ecommerceEnabled}
      />
      <FinesGridSkeleton pageSize={pageSize} />
    </>
  );
};
